// src/pages/analytics/OrderSnapshotPanel.tsx
//
// Daily Order Snapshot (recipe daily-order-snapshot, flag
// analytics.ordersnapshot, agent 3.20.0+) — open orders for one delivery
// date, before they're invoiced: by item category and by driver. Rendered
// under the Daily Snapshot. First load omits the date; the server picks the
// next delivery date with open orders and echoes it back (plain string).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, RefreshCw, AlertTriangle } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);

export function OrderSnapshotPanel() {
  const [date, setDate] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['analyticsOrderSnapshot', date],
    queryFn: () => api.getDailyOrderSnapshot(date ? { date } : {}),
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data;
  const t = data?.totals;
  const th = 'px-3 py-2 text-right text-slate-400 font-medium whitespace-nowrap';
  const td = 'px-3 py-1.5 text-right tabular-nums whitespace-nowrap';

  return (
    <section className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><ClipboardList size={16} /> Order Snapshot</h3>
          <p className="text-[11px] text-slate-500">Open orders (not yet invoiced) for one delivery date</p>
        </div>
        <div className="flex items-center gap-2">
          <DateField value={date ?? data?.date ?? ''} onChange={(v) => setDate(v || null)} className="w-[130px]" />
          <button onClick={() => q.refetch()} disabled={q.isLoading}
            className="p-1.5 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white disabled:opacity-50">
            <RefreshCw size={12} className={q.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {q.error && (
        <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-xs flex items-center gap-2">
          <AlertTriangle size={14} /> Couldn't load orders: {(q.error as Error).message}
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
      ) : data && t && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              ['Orders', fmtNum(t.orders)], ['Customers', fmtNum(t.customers)], ['Lines', fmtNum(t.lines)],
              ['Ordered $', fmtCur(t.dollars)], ['On hold', fmtNum(t.onHold)],
            ].map(([label, value]) => (
              <div key={label} className="p-3 rounded-lg border border-slate-700/40 bg-slate-900/30">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</div>
                <div className="text-lg font-semibold text-white tabular-nums">{value}</div>
              </div>
            ))}
          </div>
          {t.orders === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">No open orders for {data.date}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-700/40 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/50">
                      <th className="px-3 py-2 text-left text-slate-400 font-medium">Item Category</th>
                      <th className={th}>Orders</th><th className={th}>Qty</th><th className={th}>$</th><th className={th}>% of $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byCategory.map(c => (
                      <tr key={c.category} className="border-b border-slate-800/50">
                        <td className="px-3 py-1.5 text-white">{c.category}</td>
                        <td className={`${td} text-slate-400`}>{fmtNum(c.orders)}</td>
                        <td className={`${td} text-slate-300`}>{fmtNum(c.qty)}</td>
                        <td className={`${td} text-white`}>{fmtCur(c.dollars)}</td>
                        <td className={`${td} text-slate-400`}>{c.pctOfDollars.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg border border-slate-700/40 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/50">
                      <th className="px-3 py-2 text-left text-slate-400 font-medium">Driver</th>
                      <th className={th}>Orders</th><th className={th}>Stops</th><th className={th}>$</th><th className={th}>On hold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byDriver.map(dr => (
                      <tr key={dr.driver} className="border-b border-slate-800/50">
                        <td className="px-3 py-1.5 text-white">{dr.driver}</td>
                        <td className={`${td} text-slate-400`}>{fmtNum(dr.orders)}</td>
                        <td className={`${td} text-slate-300`}>{fmtNum(dr.customers)}</td>
                        <td className={`${td} text-white`}>{fmtCur(dr.dollars)}</td>
                        <td className={`${td} ${dr.onHold > 0 ? 'text-amber-400' : 'text-slate-500'}`}>{dr.onHold}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
